import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import axios from 'axios';
import * as tenantService from '../../services/tenantService';
import PageHeader from '../shell/PageHeader';
import { DataTable, FormSection, Field, Notice } from '../patterns';
import type { DataTableColumn } from '../patterns';
import RemapPestModal from './RemapPestModal';

interface OrphanEntry { code: string; count?: number; }
interface IntegrityReport {
  tenantId: string;
  scoutingOrphans: OrphanEntry[];
  configOrphans: OrphanEntry[];
}

const SystemIntegrityPage: React.FC = () => {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [report, setReport] = useState<IntegrityReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [remapTarget, setRemapTarget] = useState<{ code: string } | null>(null);

  useBreadcrumb([
    { label: t('nav.system.module'), to: '/system' },
    { label: t('system.integrity.title') },
  ]);

  useEffect(() => {
    tenantService.listTenants()
      .then(ts => setTenants((ts as any[]).filter(tn => tn.tenantId !== 'system')))
      .catch(() => { /* non-critical */ });
  }, []);

  const runCheck = useCallback(async () => {
    if (!selectedTenantId) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    setReport(null);
    try {
      const res = await axios.post(
        `/api/v1/integrity/check/${encodeURIComponent(selectedTenantId)}`,
        {},
        { withCredentials: true }
      );
      setReport(res.data as IntegrityReport);
    } catch (err: any) {
      setError(err?.response?.data?.error || t('system.integrity.errors.checkFailed'));
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, t]);

  const totalOrphans = report ? report.scoutingOrphans.length + report.configOrphans.length : 0;

  const scoutingCols: DataTableColumn<OrphanEntry>[] = [
    { key: 'code',  label: t('system.integrity.scouting.code'),     render: r => <code style={{ fontFamily: 'monospace' }}>{r.code}</code> },
    { key: 'count', label: t('system.integrity.scouting.affected'), render: r => r.count ?? '—' },
    {
      key: 'action',
      label: '',
      render: r => (
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          onClick={() => setRemapTarget({ code: r.code })}
        >
          {t('system.integrity.remapAction')}
        </button>
      ),
    },
  ];

  const configCols: DataTableColumn<OrphanEntry>[] = [
    { key: 'code',  label: t('system.integrity.config.code'), render: r => <code style={{ fontFamily: 'monospace' }}>{r.code}</code> },
    {
      key: 'action',
      label: '',
      render: r => (
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          onClick={() => setRemapTarget({ code: r.code })}
        >
          {t('system.integrity.remapAction')}
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('system.integrity.title')} meta={t('system.integrity.subtitle')} />

      {error && <Notice tone="danger">{error}</Notice>}
      {success && <Notice tone="success">{success}</Notice>}

      <FormSection id="check" title={t('system.integrity.runCheck')}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 260 }}>
            <Field label={t('system.integrity.tenantLabel')} controlId="integrity-tenant">
              <select
                id="integrity-tenant"
                className="pat-input"
                value={selectedTenantId}
                onChange={e => { setSelectedTenantId(e.target.value); setReport(null); }}
              >
                <option value="">{t('system.integrity.tenantPlaceholder')}</option>
                {tenants.map(tn => (
                  <option key={tn._id} value={tn.tenantId}>{tn.name}</option>
                ))}
              </select>
            </Field>
          </div>
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={runCheck}
            disabled={!selectedTenantId || loading}
          >
            {loading ? t('system.integrity.checking') : t('system.integrity.runCheck')}
          </button>
        </div>
      </FormSection>

      {!report && !loading && (
        <div className="pat-data-table pat-data-table--empty" style={{ marginTop: 16 }}>
          {t('system.integrity.selectFirst')}
        </div>
      )}

      {report && totalOrphans === 0 && (
        <Notice tone="success" className="mt-3">
          {t('system.integrity.noOrphans')}
        </Notice>
      )}

      {report && totalOrphans > 0 && (
        <>
          <Notice tone="warn" className="mt-3">
            {t('system.integrity.foundOrphans', { count: totalOrphans })}
          </Notice>

          {report.scoutingOrphans.length > 0 && (
            <FormSection id="scouting" title={t('system.integrity.scouting.title')}>
              <DataTable
                columns={scoutingCols}
                rows={report.scoutingOrphans}
                rowKey={r => r.code}
              />
            </FormSection>
          )}

          {report.configOrphans.length > 0 && (
            <FormSection id="config" title={t('system.integrity.config.title')}>
              <DataTable
                columns={configCols}
                rows={report.configOrphans}
                rowKey={r => r.code}
              />
            </FormSection>
          )}
        </>
      )}

      {remapTarget && (
        <RemapPestModal
          show={!!remapTarget}
          tenantId={selectedTenantId}
          orphanCode={remapTarget.code}
          onHide={() => setRemapTarget(null)}
          onRemapped={result => {
            setRemapTarget(null);
            setSuccess(t('system.integrity.success.remapped', {
              from: result.from,
              to: result.to,
              records: result.records,
              configs: result.configs,
            }));
            runCheck(); // refresh
          }}
        />
      )}
    </>
  );
};

export default SystemIntegrityPage;
